<template>
  <div class="waste-visualization">
    <div class="space-y-4">
      <div class="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg">
        <h2 class="text-xl font-bold mb-2">♻️ Mesure du Gaspillage</h2>
        <p class="text-sm opacity-90">Saisie du poids total gaspillé</p>
      </div>

      <!-- Saisie poids global -->
      <div class="bg-white rounded-xl p-6 shadow-sm">
        <h3 class="font-bold text-gray-800 mb-4 text-center">⚖️ Poids total gaspillé</h3>

        <div class="global-input-wrapper">
          <input
            type="number"
            min="0"
            step="0.01"
            v-model.number="globalWeight"
            class="global-weight-input"
            :class="globalWeight > 0 ? 'global-weight-input--active' : ''"
            placeholder="0.00"
          />
          <span class="global-unit-label">kg</span>
        </div>

        <p class="text-sm text-gray-500 text-center mt-3">
          Pesez l'ensemble des restes alimentaires après le service
        </p>

        <div class="mt-4 bg-red-50 rounded-lg p-4 text-center border-2 border-red-300">
          <p class="text-sm text-gray-600 mb-1">Total gaspillé</p>
          <p class="text-4xl font-bold text-red-600">{{ totalWaste }} kg</p>
        </div>
      </div>

      <!-- Photos des poubelles -->
      <div class="bg-white rounded-xl p-4 shadow-sm">
        <h3 class="font-bold text-gray-800 mb-3">📸 Photos des poubelles</h3>
        <div class="flex gap-2 flex-wrap">
          <div
            v-for="(photo, idx) in photos"
            :key="idx"
            class="relative w-24 h-24 bg-gray-100 rounded-lg flex items-center justify-center"
          >
            <span class="text-3xl">🗑️</span>
            <button
              @click="removePhoto(idx)"
              class="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs"
            >
              ✕
            </button>
          </div>
          <button
            @click="addPhoto"
            class="w-24 h-24 bg-blue-50 border-2 border-blue-300 border-dashed rounded-lg flex items-center justify-center hover:bg-blue-100"
          >
            <span class="text-3xl text-blue-500">📷</span>
          </button>
        </div>
      </div>

      <!-- Boutons d'action -->
      <div class="grid grid-cols-2 gap-3">
        <button
          @click="emitResult('display')"
          :disabled="parseFloat(totalWaste) === 0"
          :class="parseFloat(totalWaste) > 0 ? 'bg-green-500 hover:bg-green-600' : 'bg-gray-300 cursor-not-allowed'"
          class="text-white rounded-lg p-4 font-semibold shadow-md transition"
        >
          👀 Voir le résultat
        </button>
        <button
          @click="emitResult('poster')"
          :disabled="parseFloat(totalWaste) === 0"
          :class="parseFloat(totalWaste) > 0 ? 'bg-blue-500 hover:bg-blue-600' : 'bg-gray-300 cursor-not-allowed'"
          class="text-white rounded-lg p-4 font-semibold shadow-md transition"
        >
          🖨️ Préparer affiche
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "WasteVisualizationSimple",
  data() {
    return {
      globalWeight: 0,
      photos: [],
    }
  },
  computed: {
    totalWaste() {
      return (parseFloat(this.globalWeight) || 0).toFixed(2)
    },
  },
  methods: {
    addPhoto() {
      this.photos.push({})
    },
    removePhoto(idx) {
      this.photos.splice(idx, 1)
    },
    emitResult(displayMode) {
      this.$emit("waste-saved", {
        type: "simple",
        displayMode,
        categories: [],
        total_weight_kg: parseFloat(this.totalWaste),
      })
    },
  },
}
</script>

<style scoped>
.global-input-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 1.5rem;
  background-color: #fff7ed;
  border: 2px solid #fdba74;
  border-radius: 0.75rem;
}
.global-weight-input {
  width: 10rem;
  border: 2px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  font-size: 2rem;
  font-weight: 700;
  text-align: right;
  transition: border-color 0.15s;
  background-color: #ffffff;
}
.global-weight-input:focus {
  outline: none;
  border-color: #3b82f6;
}
.global-weight-input--active {
  border-color: #fdba74;
  color: #ea580c;
}
.global-unit-label {
  font-size: 2rem;
  font-weight: 700;
  color: #6b7280;
}
.space-y-4 > * + * {
  margin-top: 1rem;
}
.gap-2 {
  gap: 0.5rem;
}
.gap-3 {
  gap: 0.75rem;
}
.mt-3 {
  margin-top: 0.75rem;
}
.mt-4 {
  margin-top: 1rem;
}
.mb-1 {
  margin-bottom: 0.25rem;
}
.mb-3 {
  margin-bottom: 0.75rem;
}
.mb-4 {
  margin-bottom: 1rem;
}
.grid {
  display: grid;
}
.grid-cols-2 {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}
.flex {
  display: flex;
}
.flex-wrap {
  flex-wrap: wrap;
}
.items-center {
  align-items: center;
}
.justify-center {
  justify-content: center;
}
.relative {
  position: relative;
}
.absolute {
  position: absolute;
}
.-top-2 {
  top: -0.5rem;
}
.-right-2 {
  right: -0.5rem;
}
.text-center {
  text-align: center;
}
.bg-gradient-to-br {
  background-image: linear-gradient(to bottom right, var(--tw-gradient-stops));
}
.from-green-500 {
  --tw-gradient-from: #22c55e;
  --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to);
}
.to-green-600 {
  --tw-gradient-to: #16a34a;
}
.rounded-xl {
  border-radius: 0.75rem;
}
.rounded-lg {
  border-radius: 0.5rem;
}
.rounded-full {
  border-radius: 9999px;
}
.shadow-sm {
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
}
.shadow-md {
  box-shadow: 0 2px 4px -2px rgb(0 0 0 / 0.1);
}
.shadow-lg {
  box-shadow: 0 4px 6px -4px rgb(0 0 0 / 0.1);
}
.border-2 {
  border-width: 2px;
}
.border-dashed {
  border-style: dashed;
}
.border-blue-300 {
  border-color: #93c5fd;
}
.border-red-300 {
  border-color: #fca5a5;
}
.border-red-500 {
  border-color: #ef4444;
}
.bg-white {
  background-color: #ffffff;
}
.bg-red-50 {
  background-color: #fef2f2;
}
.bg-red-500 {
  background-color: #ef4444;
}
.bg-blue-50 {
  background-color: #eff6ff;
}
.hover\:bg-blue-100:hover {
  background-color: #dbeafe;
}
.bg-blue-500 {
  background-color: #3b82f6;
}
.hover\:bg-blue-600:hover {
  background-color: #2563eb;
}
.bg-green-500 {
  background-color: #22c55e;
}
.hover\:bg-green-600:hover {
  background-color: #16a34a;
}
.bg-gray-100 {
  background-color: #f3f4f6;
}
.bg-gray-300 {
  background-color: #d1d5db;
}
.text-white {
  color: #ffffff;
}
.text-blue-500 {
  color: #3b82f6;
}
.text-red-600 {
  color: #dc2626;
}
.text-gray-500 {
  color: #6b7280;
}
.text-gray-600 {
  color: #4b5563;
}
.text-gray-800 {
  color: #1f2937;
}
.font-bold {
  font-weight: 700;
}
.font-semibold {
  font-weight: 600;
}
.text-xs {
  font-size: 0.75rem;
  line-height: 1rem;
}
.text-sm {
  font-size: 0.875rem;
  line-height: 1.25rem;
}
.text-xl {
  font-size: 1.25rem;
  line-height: 1.75rem;
}
.text-3xl {
  font-size: 1.875rem;
  line-height: 2.25rem;
}
.text-4xl {
  font-size: 2.25rem;
  line-height: 2.5rem;
}
.p-4 {
  padding: 1rem;
}
.p-6 {
  padding: 1.5rem;
}
.w-6 {
  width: 1.5rem;
}
.w-24 {
  width: 6rem;
}
.h-6 {
  height: 1.5rem;
}
.h-24 {
  height: 6rem;
}
.opacity-90 {
  opacity: 0.9;
}
.cursor-not-allowed {
  cursor: not-allowed;
}
.transition {
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
}
button {
  cursor: pointer;
  border: none;
  background: none;
}
button:not(:disabled):hover {
  opacity: 0.9;
}
</style>
